import type { ToolbarDisabled } from '@evisort/eds/components/Toolbar/types';
import type { IconType } from '@evisort/eds/types/SharedIconProps';

import {
  PropsWithChildren,
  Suspense,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useAppShell } from './AppShellContext';
import {
  AppShellRailSlot,
  useAppShellSlotRegistry,
} from './AppShellSlotRegistry';

const useSlotContainer = () =>
  useState(() => {
    const container = document.createElement('div');
    container.style.display = 'contents';
    return container;
  })[0];

export function AppShellHeader({ children }: PropsWithChildren) {
  const { registerHeader } = useAppShellSlotRegistry();
  const container = useSlotContainer();

  useLayoutEffect(() => registerHeader(container), [registerHeader, container]);

  return createPortal(children, container);
}

export function AppShellSecondaryHeader({ children }: PropsWithChildren) {
  const { registerSecondaryHeader } = useAppShellSlotRegistry();
  const container = useSlotContainer();

  useLayoutEffect(
    () => registerSecondaryHeader(container),
    [registerSecondaryHeader, container],
  );

  return createPortal(children, container);
}

interface AppShellPanelProps extends PropsWithChildren {
  panelKey: string;
  order: number;
  icon?: IconType;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  count?: number;
  maxCount?: number;
  width?: 'fill';
  actionOnly?: boolean;
  onOpen?: () => void;
}

export function AppShellPanel({
  panelKey,
  order,
  icon,
  label,
  tooltip,
  disabled,
  count,
  maxCount,
  width,
  actionOnly,
  onOpen,
  children,
}: AppShellPanelProps) {
  const { registerPanel, unregisterPanel } = useAppShellSlotRegistry();
  const { mountedPanelKeys } = useAppShell();
  const container = useSlotContainer();

  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const handleOpen = useCallback(() => onOpenRef.current?.(), []);

  useLayoutEffect(() => {
    registerPanel({
      panelKey,
      order,
      icon,
      label,
      tooltip,
      disabled,
      count,
      maxCount,
      width,
      actionOnly,
      onOpen: handleOpen,
      container,
    });
  }, [
    registerPanel,
    panelKey,
    order,
    icon,
    label,
    tooltip,
    disabled,
    count,
    maxCount,
    width,
    actionOnly,
    handleOpen,
    container,
  ]);

  useLayoutEffect(
    () => () => unregisterPanel(panelKey),
    [unregisterPanel, panelKey],
  );

  if (!mountedPanelKeys?.has(panelKey)) {
    return null;
  }

  return createPortal(
    <Suspense fallback={null}>{children}</Suspense>,
    container,
  );
}

interface UseAppShellRailOptions {
  disabled?: ToolbarDisabled;
  defaultPanelKey?: string;
  keepPanelsMounted?: boolean;
  initialPanelWidth?: number;
  forcedPanelWidth?: number;
  onPanelWidthChange?: (width: number) => void;
}

export function useAppShellRail({
  disabled = false,
  defaultPanelKey,
  keepPanelsMounted = false,
  initialPanelWidth,
  forcedPanelWidth,
  onPanelWidthChange,
}: UseAppShellRailOptions = {}) {
  const { registerRail, unregisterRail } = useAppShellSlotRegistry();

  const isDisabled = disabled !== false;
  const disabledReason = disabled === false ? '' : disabled.disabledReason;

  useLayoutEffect(() => {
    const slot: AppShellRailSlot = {
      disabled: isDisabled ? { disabled: true, disabledReason } : false,
      defaultPanelKey,
      keepPanelsMounted,
      initialPanelWidth,
      forcedPanelWidth,
      onPanelWidthChange,
    };
    registerRail(slot);
  }, [
    registerRail,
    isDisabled,
    disabledReason,
    defaultPanelKey,
    keepPanelsMounted,
    initialPanelWidth,
    forcedPanelWidth,
    onPanelWidthChange,
  ]);

  useLayoutEffect(() => () => unregisterRail(), [unregisterRail]);
}
